import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { TableDataProps } from "@/pages/TimeEntries";
import { EditTimeEntryLocationBtn, ShowTimeEntryBtn } from "../Show";
import { Badge } from "@/components/ui/badge";
import { TimeEntryTypeBadge } from "@/components/TimeEntryTypeBadge";
import LogoFavicon from "../../../../public/images/logo-favicon.png";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();

  // Depois de corrigir o local, a linha reflete o novo "dentro da cerca"/endereço.
  const refreshRow = (uuid: string, entry: any) => {
    if (!entry) return;
    updateFront?.((prev) => ({
      ...prev,
      data: prev?.data?.map((row) =>
        row.uuid === uuid
          ? {
              ...row,
              latitude: entry.latitude,
              longitude: entry.longitude,
              address: entry.address,
              within_geofence: entry.within_geofence,
            }
          : row
      ),
    }));
  };
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Foto</TableHead>
            <TableHead>Colaborador</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Horário da Batida</TableHead>
            <TableHead>Setor</TableHead>
            <TableHead className="text-center">Dentro da Cerca</TableHead>
            <TableHead className="text-center">Atraso</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item) => {
            return (
              <TableRow key={item?.id}>
                <TableCell>
                  <img
                    src={item?.photo_path || item?.employee?.user?.photo || LogoFavicon}
                    alt={item?.employee?.user?.name || ""}
                    className="size-8 aspect-square object-cover rounded-full border"
                  />
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.employee?.user?.name}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  <TimeEntryTypeBadge type={item?.time_entry_type_id} />
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.clocked_at
                    ? new Date(item.clocked_at).toLocaleString("pt-BR")
                    : "-"}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.employee?.sector?.name || "Não informado"}
                </TableCell>
                <TableCell className="text-center">
                  {item?.within_geofence === null ? (
                    <Badge variant="outline">-</Badge>
                  ) : item?.within_geofence ? (
                    <Badge variant="default">Sim</Badge>
                  ) : (
                    <Badge variant="destructive">Fora da Cerca</Badge>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {item?.is_late === null ? (
                    <Badge variant="outline">-</Badge>
                  ) : item?.is_late ? (
                    <Badge variant="destructive">Atrasado</Badge>
                  ) : (
                    <Badge variant="default">No horário</Badge>
                  )}
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("show-time-entry-details")}>
                      <ShowTimeEntryBtn
                        uuid={item?.uuid}
                        onSaved={(entry) => refreshRow(item?.uuid, entry)}
                      />
                    </If>
                    <If
                      condition={
                        item?.within_geofence === false &&
                        hasPermission("edit-time-entry-location")
                      }
                    >
                      <EditTimeEntryLocationBtn
                        uuid={item?.uuid}
                        onSaved={(entry) => refreshRow(item?.uuid, entry)}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={8}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
